/**
 * Die Auswahl der Anlagenart — als Pillen mit Symbol.
 *
 * Die Form stammt aus `templates/site-settings/SiteSettings.dc.html`: sechs
 * Pillen nebeneinander, die gewählte im Grün der Marke, jede mit dem Symbol
 * ihrer Art. Sie stehen in einer Reihe und nicht in einem Raster, weil die
 * Beschriftungen verschieden lang sind — ein Raster ließe neben „Wohnung" so
 * viel Luft wie neben „Mehrfamilienhaus".
 *
 * **Warum kein `Select`:** Ein natives `<select>` zeigt in seinen Optionen
 * ausschließlich Text. Das Symbol steht aber im Umschalter und in der
 * Anlagenliste, und wer eine Art auswählt, soll sehen, was er später
 * wiedererkennt. Sechs Möglichkeiten passen ohne Aufklappen auf die Seite; ab
 * etwa einem Dutzend wäre die Liste die bessere Form.
 *
 * **Es bleibt eine echte Radiogruppe.** Unter jeder Pille liegt ein
 * `<input type="radio">` — dieselbe Bauweise wie `Checkbox` im Design-System:
 * `opacity: 0` statt `display: none`, damit Tastatur und Sprachausgabe es
 * behalten. `<fieldset>` und `<legend>` sagen, wozu die sechs gehören; ein
 * `<div>` mit Überschrift täte das nicht.
 */
import { useId, useState } from "react";
import { fontWeight } from "@hausmeter/ui";
import { SITE_KINDS, SITE_KIND_LABELS, siteKindIcon, type SiteKind } from "../lib/site-kind.js";

export interface SiteKindFieldProps {
  readonly value: SiteKind;
  readonly onChange: (kind: SiteKind) => void;
}

export function SiteKindField({ value, onChange }: SiteKindFieldProps) {
  const group = useId();
  /** Welche Pille den Fokus hat — der Ring liegt auf ihr, nicht auf dem Feld. */
  const [focused, setFocused] = useState<SiteKind | null>(null);

  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, flex: "1 1 100%", minWidth: 0 }}>
      {/* Dieselbe Beschriftung wie an `Input` und `Select` — nicht die 12,5 px
          der Vorlage: In einer Zeile mit den Feldern daneben fiele der
          Unterschied als Fehler auf, nicht als Absicht. */}
      <legend
        /* Seit HM-162 steht die Beschriftung links in der Zeile der Einstellungen
           (`SettingRow`); die Legende bleibt für Sprachausgaben und ist sonst unsichtbar. */
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", padding: 0 }}
      >
        Art der Anlage
      </legend>

      {/* Ein Kachelraster wie im überarbeiteten Entwurf (HM-162): 44 px hoch, die
          Mindestgröße allein stehender Schalter im Design-System. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 190px), 1fr))", gap: 8 }}>
        {SITE_KINDS.map((kind) => {
          const KindIcon = siteKindIcon(kind);
          const selected = kind === value;

          return (
            <label
              key={kind}
              style={{
                /* Notwendig, nicht kosmetisch: Das `<input>` darunter liegt
                   `absolute` und bezöge sich sonst auf die Seite — unsichtbar,
                   aber anklickbar, und dort fängt es fremde Klicks ab. */
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 12px",
                minHeight: 44,
                borderRadius: "var(--radius-md)",
                border: `1px solid ${selected ? "var(--border-brand)" : "var(--border-default)"}`,
                background: selected ? "var(--hm-green-50)" : "var(--surface-card)",
                color: selected ? "var(--hm-green-700)" : "var(--text-body)",
                fontSize: 13,
                fontWeight: selected ? fontWeight.bold : fontWeight.medium,
                boxShadow:
                  focused === kind
                    ? "0 0 0 var(--ring-width) var(--focus-ring)"
                    : selected
                      ? "0 0 0 1px var(--border-brand)"
                      : "none",
                cursor: "pointer",
                transition: "border-color .15s ease, background .15s ease, box-shadow .15s ease",
              }}
            >
              <input
                type="radio"
                name={group}
                value={kind}
                checked={selected}
                onChange={() => onChange(kind)}
                onFocus={() => setFocused(kind)}
                onBlur={() => setFocused(null)}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  margin: 0,
                  opacity: 0,
                  cursor: "inherit",
                }}
              />
              <KindIcon
                size={18}
                style={{ flexShrink: 0, color: selected ? undefined : "var(--text-muted)" }}
              />
              {SITE_KIND_LABELS[kind]}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
